import { useId, useMemo } from "react";
import { z } from "zod";

import { ColocaForm } from "@/shared/components/forms/ColocaForm";
import { ColocaInputField } from "@/shared/components/forms/ColocaInputField";
import { ColocaSelectField } from "@/shared/components/forms/ColocaSelectField";
import { useZodForm } from "@/shared/components/forms/useZodForm";
import useTranslations from "@/shared/lang/useTranslations";

import { PSE_DOCUMENT_TYPES, PSE_FIELD_MAX } from "./pse/psePayer.schema";
import { PsePaymentVoucher } from "./pse/PsePaymentVoucher";

/**
 * What the Nequi push and Botón Bancolombia corridors need about the payer. Same field rules as the PSE
 * contract (`psePayer.schema.ts` — the Colombian networks share the maximums and the document catalog), minus
 * what only PSE has: no bank picker (the button IS the bank; Nequi is a wallet) and no person-type mandate,
 * so the full document list is offered.
 */
export interface ColombiaPayerValues {
  documentType: string;
  documentNumber: string;
  payerName: string;
  email: string;
  phone: string;
  address: string;
}

export interface ColombiaPayerStepProps {
  /** HTML id of the form — the host renders the submit button in its own footer, wired with `form={formId}`. */
  formId: string;
  /** Values to restore when the step is re-entered from the confirmation screen. */
  defaults: Partial<ColombiaPayerValues>;
  reference: string;
  /** Amount already formatted for display, or `null` until the operation has been priced. */
  amount: string | null;
  currency: string;
  /** Rail-specific phone label (Nequi: the number IS the account charged). Falls back to the generic one. */
  phoneLabel?: string;
  /** Fires only with a valid payer. */
  onSubmit: (values: ColombiaPayerValues) => void;
}

/**
 * The payer step of the non-PSE Colombian payer rails, complete: schema, document catalog and fields.
 * Deliberately a sibling of `PsePayerStep` rather than a variant of it — the PSE form is shaped by the
 * network's certification checklist and must not move when other rails evolve.
 */
export function ColombiaPayerStep({
  formId,
  defaults,
  reference,
  amount,
  currency,
  phoneLabel,
  onSubmit,
}: ColombiaPayerStepProps) {
  const { t } = useTranslations("payins");
  const { t: tGlobal } = useTranslations("global");
  const identityHeadingId = useId();
  const contactHeadingId = useId();

  const schema = useMemo(
    () =>
      z.object({
        documentType: z.enum(PSE_DOCUMENT_TYPES),
        documentNumber: z.string().regex(/^\d{4,15}$/, t("pse_document_invalid")),
        payerName: z
          .string()
          .trim()
          .min(2, tGlobal("input_error_required"))
          .max(PSE_FIELD_MAX.payerName, t("pse_max_length", { max: String(PSE_FIELD_MAX.payerName) })),
        email: z
          .string()
          .trim()
          .email(t("pse_email_invalid"))
          .max(PSE_FIELD_MAX.email, t("pse_max_length", { max: String(PSE_FIELD_MAX.email) })),
        // Stricter than PSE's 7–10, where the phone is only contact data: on these rails the number is the
        // account being charged, and a malformed one is refused by the network, not by us.
        phone: z.string().regex(/^3\d{9}$/, t("mobile_phone_invalid")),
        address: z
          .string()
          .trim()
          .min(3, tGlobal("input_error_required"))
          .max(PSE_FIELD_MAX.address, t("pse_max_length", { max: String(PSE_FIELD_MAX.address) })),
      }),
    [t, tGlobal],
  );
  const form = useZodForm(schema, {
    documentType: (PSE_DOCUMENT_TYPES as readonly string[]).includes(defaults.documentType ?? "")
      ? (defaults.documentType as (typeof PSE_DOCUMENT_TYPES)[number])
      : "CC",
    documentNumber: defaults.documentNumber ?? "",
    payerName: defaults.payerName ?? "",
    email: defaults.email ?? "",
    phone: defaults.phone ?? "",
    address: defaults.address ?? "",
  });

  return (
    <ColocaForm id={formId} form={form} className="space-y-0" onSubmit={onSubmit}>
      <div className="space-y-5">
        <PsePaymentVoucher amount={amount} currency={currency} reference={reference} />

        <section aria-labelledby={identityHeadingId} className="space-y-3">
          <h3
            id={identityHeadingId}
            className="font-mono text-[10px] uppercase tracking-[0.16em] text-muted-foreground"
          >
            {t("pse_group_identity")}
          </h3>

          {/* `items-start` for the same reason as in PsePayerFields: without it the stretching left cell
              drops its select half a line below the input as soon as this row grows. */}
          <div className="grid grid-cols-[7.5rem_1fr] items-start gap-3">
            {/* All six fields are required — the schema above declares no `.optional()`. */}
            <ColocaSelectField
              name="documentType"
              label={t("pse_document_type_label")}
              required
              items={PSE_DOCUMENT_TYPES.map((code) => ({ value: code, label: code }))}
            />
            <ColocaInputField
              name="documentNumber"
              label={t("pse_document_number_label")}
              required
              inputMode="numeric"
              maxLength={PSE_FIELD_MAX.documentNumber}
              autoComplete="off"
              placeholder={t("pse_document_placeholder")}
              transform={(raw) => raw.replace(/[^\d]/g, "")}
            />
          </div>

          <ColocaInputField
            name="payerName"
            label={t("pse_name_label")}
            required
            maxLength={PSE_FIELD_MAX.payerName}
            autoComplete="name"
            placeholder={t("pse_name_placeholder")}
          />
        </section>

        <section aria-labelledby={contactHeadingId} className="space-y-3">
          <h3
            id={contactHeadingId}
            className="font-mono text-[10px] uppercase tracking-[0.16em] text-muted-foreground"
          >
            {t("pse_group_contact")}
          </h3>

          <ColocaInputField
            name="email"
            type="email"
            label={t("pse_email_label")}
            required
            maxLength={PSE_FIELD_MAX.email}
            autoComplete="email"
            placeholder={t("pse_email_placeholder")}
          />
          <ColocaInputField
            name="phone"
            label={phoneLabel ?? t("pse_phone_label")}
            required
            inputMode="tel"
            maxLength={10}
            autoComplete="tel-national"
            placeholder={t("pse_phone_placeholder")}
            transform={(raw) => raw.replace(/[^\d]/g, "")}
          />
          <ColocaInputField
            name="address"
            label={t("pse_address_label")}
            required
            maxLength={PSE_FIELD_MAX.address}
            autoComplete="street-address"
            placeholder={t("pse_address_placeholder")}
          />
        </section>
      </div>
    </ColocaForm>
  );
}
